import axiosWrapper from "@Services/axiosWrapper";
import { getBackendUrl } from "@Utils";
import { v4 as uuid } from "uuid";
import type { AxiosProgressEvent } from "axios";
import type { UploadFileInTempResponse } from "@ServerTypes/fileUpload/fileUpload.schema";
import type { UploadSimulationVideoExportResponse } from "@ServerTypes/schedule/schedule.schema";
/**
 * A temp-upload file name whose EXTENSION describes the bytes, for content that is a `data:` URL.
 *
 * The portal's file-type allow-list is applied to every temp promotion by extension, so an avatar
 * uploaded as `profile-pic.txt` would be gated on `.txt` — disabling every image type in the portal
 * would not block it, and disabling `.txt` would. The name has to say what the bytes are.
 *
 * The CONTENT gate is unaffected either way: a `data:` URL is a text blob no signature matches, so it
 * is UNDETECTABLE and passes regardless of the extension claimed here.
 */
const DATA_URL_EXTENSIONS: Record<string, string> = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/jpg": ".jpg",
  "image/webp": ".webp",
  "image/gif": ".gif",
  "image/svg+xml": ".svg",
};

export const dataUrlFileName = (value: string, baseName: string): string => {
  const mimeType = /^data:([^;,]+)/.exec(value)?.[1]?.toLowerCase() ?? "";
  return `${baseName}${DATA_URL_EXTENSIONS[mimeType] ?? ".png"}`;
};

export interface UploadStringToTempOptions {
  uploadType?: string;
  onProgress?: (event: { progress: number }) => void;
  abortSignal?: AbortSignal;
}

export const uploadStringToTemp = async (
  content: string | Blob,
  filename: string,
  optsOrUploadType?: string | UploadStringToTempOptions,
) => {
  if (!content) {
    return null;
  }

  const opts: UploadStringToTempOptions =
    typeof optsOrUploadType === "string" ? { uploadType: optsOrUploadType } : (optsOrUploadType ?? {});

  const extensionIndex = filename.lastIndexOf(".");
  const baseName = extensionIndex === -1 ? filename : filename.slice(0, extensionIndex);
  const extension = extensionIndex === -1 ? "" : filename.slice(extensionIndex);
  const uniqueFilename = `${baseName}-${uuid()}${extension}`;

  const formData = new FormData();
  const blob = content instanceof Blob ? content : new Blob([content], { type: "text/plain" });
  formData.append("uploadFiles", blob, uniqueFilename);

  const url = opts.uploadType
    ? `${getBackendUrl()}/fileSystemServer/uploadFileInTemp?type=${encodeURIComponent(opts.uploadType)}`
    : `${getBackendUrl()}/fileSystemServer/uploadFileInTemp`;
  // `AxiosRequestConfig` declares `signal?`/`onUploadProgress?` without `undefined`, and axios
  // reads these keys by presence, so the optional halves of the config are spread in only when the
  // caller actually supplied them rather than being set to an explicit `undefined`.
  const { abortSignal, onProgress } = opts;
  const response = await axiosWrapper.post<UploadFileInTempResponse>(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    ...(abortSignal !== undefined && { signal: abortSignal }),
    ...(onProgress !== undefined && {
      onUploadProgress: (event: AxiosProgressEvent) => {
        const total = event.total ?? 0;
        const progress = total > 0 ? event.loaded / total : 0;
        onProgress({ progress });
      },
    }),
  });

  if (response.data.status && response.data.keys?.length) {
    return response.data.keys[0];
  }

  return null;
};

// `/schedule/saveSimulationVideoExport` answers the payload-less status envelope; taken from the
// route's own schema rather than restated. The local `{ status: boolean }` this replaces widened the
// discriminant, so `status` could not narrow and a refusal body typed the same as a success one.

export const uploadSimulationVideo = async (file: File, scheduleId: string): Promise<boolean> => {
  const awsKey = await uploadStringToTemp(file, file.name);
  if (!awsKey) return false;

  const saveRes = await axiosWrapper.post<UploadSimulationVideoExportResponse>(
    `${getBackendUrl()}/schedule/saveSimulationVideoExport`,
    { awsKey, scheduleId },
  );

  return Boolean(saveRes.data.status);
};
